
import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLanguage } from '@/contexts/LanguageContext';

const ConnectionStatusBanner = () => {
  const { t } = useLanguage();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const [showOnlineAlert, setShowOnlineAlert] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOnlineAlert(true);
      // Auto-hide the online alert after 5 seconds
      setTimeout(() => setShowOnlineAlert(false), 5000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineAlert(true);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial status
    setIsOnline(navigator.onLine);
    if (!navigator.onLine) {
      setShowOfflineAlert(true);
    }

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showOfflineAlert && !showOnlineAlert) {
    return null;
  }

  return (
    <div className="sticky top-16 w-full z-40">
      {!isOnline && showOfflineAlert && (
        <Alert variant="destructive" className="rounded-none border-t border-b border-destructive/50 bg-destructive/10">
          <WifiOff className="h-4 w-4 mr-2" />
          <AlertDescription className="flex items-center">
            {t('common.offlineMessage') || 'You are currently offline. Some features may not be available.'}
            <button 
              onClick={() => setShowOfflineAlert(false)} 
              className="ml-auto text-xs hover:underline"
            >
              {t('common.dismiss') || 'Dismiss'}
            </button>
          </AlertDescription>
        </Alert>
      )}
      
      {isOnline && showOnlineAlert && (
        <Alert className="rounded-none border-t border-b border-green-500/50 bg-green-500/10">
          <Wifi className="h-4 w-4 mr-2 text-green-500" />
          <AlertDescription className="flex items-center">
            {t('common.onlineMessage') || 'Your connection has been restored.'}
            <button 
              onClick={() => setShowOnlineAlert(false)} 
              className="ml-auto text-xs hover:underline"
            >
              {t('common.dismiss') || 'Dismiss'}
            </button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ConnectionStatusBanner;
